import { useEffect, useState } from "react";
import { api, formatCurrency } from "../lib/api";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import EmptyState from "../components/Help/EmptyState";
import { Building, Sparkles, HelpCircle, ArrowUpRight } from "lucide-react";


export default function CostSegregation() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/cost-seg").then((r) => setItems(r.data));
  }, []);

  const totalBasis = items.reduce((s, i) => s + (i.building_value || 0), 0);
  const totalBonus = items.reduce((s, i) => s + (i.bonus_depreciation_available || 0), 0);
  const totalAnnual = items.reduce((s, i) => s + (i.annual_depreciation || 0), 0);

  return (
    <Layout
      title="Cost Segregation & Depreciation"
      subtitle="IRS asset classification & bonus depreciation calculator"
    >
      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-semibold flex items-center gap-1.5">
            <span>Total Depreciable Basis</span>
            <HelpTip topic="cost-segregation" />
          </div>
          <div className="text-3xl font-extrabold tracking-tight mt-1">{formatCurrency(totalBasis)}</div>
          <div className="text-xs text-[#86868B] mt-1">Excludes non-depreciable land</div>
        </div>

        <div className="card p-5 bg-[#F4F9FF] border-[#007AFF]/20">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#007AFF] font-semibold flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Sparkles size={14} /> Bonus Depreciation Available
            </span>
            <HelpTip topic="cost-segregation" />
          </div>
          <div className="text-3xl font-extrabold tracking-tight text-[#007AFF] mt-1">{formatCurrency(totalBonus)}</div>
          <div className="text-xs text-[#86868B] mt-1">60% 1st-year accelerated deduction</div>
        </div>

        <div className="card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-semibold flex items-center gap-1.5">
            <span>Annual Depreciation</span>
            <HelpTip topic="cat-depreciation" />
          </div>
          <div className="text-3xl font-extrabold tracking-tight text-[#1f8e3a] mt-1">{formatCurrency(totalAnnual)}/yr</div>
          <div className="text-xs text-[#86868B] mt-1">Estimated annual paper tax loss</div>
        </div>
      </div>

      {/* Educational Banner */}
      <div className="card p-5 mb-6 bg-[#F5F5F7]/80">
        <h4 className="font-bold text-sm mb-1.5 flex items-center gap-1.5">
          <HelpCircle size={16} className="text-[#1C3A5E]" /> About Cost Segregation for Landlords
        </h4>
        <p className="text-xs text-[#86868B] leading-relaxed">
          Standard residential rental property is depreciated straight-line over 27.5 years. A cost segregation study accelerates depreciation by reclassifying components into 5-year personal property (appliances, carpeting, specialty lighting) and 15-year land improvements (fencing, paving, shrubbery). Under current tax law, eligible short-life assets qualify for bonus depreciation, sheltering large amounts of active or passive rental income.
        </p>
      </div>

      {/* Properties Table */}
      <div className="card overflow-hidden" data-testid="cost-seg-table">
        <div className="p-4 bg-[#F5F5F7]/60 border-b border-[#E5E5EA]/60 font-semibold text-xs text-[#86868B] uppercase tracking-wider">
          Portfolio Asset Classes
        </div>
        <div className="divide-y divide-[#E5E5EA]/60">
          {items.length === 0 && (
            <div className="p-4">
              <EmptyState
                icon={Sparkles}
                title="No Cost Segregation Studies Found"
                description="Cost segregation accelerates depreciation deductions on 5-year personal property and 15-year land improvements."
                helpTopic="cost-segregation"
                helpText="Learn How Cost Segregation Works"
                testId="cost-seg-empty-state"
              />
            </div>
          )}
          {items.map((p) => (
            <div key={p.id} className="p-5 space-y-3" data-testid={`cost-seg-${p.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-base">{p.name}</div>
                  <div className="text-xs text-[#86868B]">{p.address} · Purchased: {p.purchase_date}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#86868B]">Purchase Price</div>
                  <div className="font-extrabold text-base">{formatCurrency(p.purchase_price)}</div>
                </div>
              </div>

              {/* Grid of asset classes */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                <div className="bg-[#F5F5F7] p-3 rounded-xl">
                  <div className="text-[10px] uppercase font-semibold text-[#86868B] flex items-center justify-between">
                    <span>Land (Non-Depr)</span>
                    <HelpTip topic="land-value" />
                  </div>
                  <div className="font-bold text-sm mt-0.5">{formatCurrency(p.land_value)}</div>
                </div>
                <div className="bg-[#F5F5F7] p-3 rounded-xl">
                  <div className="text-[10px] uppercase font-semibold text-[#86868B] flex items-center justify-between">
                    <span>5-Yr Property</span>
                    <HelpTip topic="cost-segregation" />
                  </div>
                  <div className="font-bold text-sm mt-0.5">{formatCurrency(p.personal_property_5yr)}</div>
                </div>
                <div className="bg-[#F5F5F7] p-3 rounded-xl">
                  <div className="text-[10px] uppercase font-semibold text-[#86868B] flex items-center justify-between">
                    <span>15-Yr Land Impr</span>
                    <HelpTip topic="cost-segregation" />
                  </div>
                  <div className="font-bold text-sm mt-0.5">{formatCurrency(p.land_improvements_15yr)}</div>
                </div>
                <div className="bg-[#F5F5F7] p-3 rounded-xl">
                  <div className="text-[10px] uppercase font-semibold text-[#86868B] flex items-center justify-between">
                    <span>27.5-Yr Building</span>
                    <HelpTip topic="cat-depreciation" />
                  </div>
                  <div className="font-bold text-sm mt-0.5">{formatCurrency(p.building_value)}</div>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs pt-1 border-t border-[#E5E5EA]/40">

                <span className="text-[#007AFF] font-semibold">Bonus Dep (Year 1): {formatCurrency(p.bonus_depreciation_available)}</span>
                <span className="text-[#1f8e3a] font-semibold">Annual Dep: {formatCurrency(p.annual_depreciation)}/yr</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
